import type {
  InteractionActionId,
  InteractionObjectState,
  InteractionProfileId,
  InteractionTargetRef,
} from "@endclock/shared";

export const DEFAULT_INTERACTION_SEARCH_RADIUS = 4;
export const TARGET_SWITCH_MARGIN = 0.3;

/** A world-space, currently actionable descriptor prepared by the world streamer. */
export interface InteractionCandidate {
  readonly target: InteractionTargetRef;
  readonly objectType: string;
  readonly profileId: InteractionProfileId;
  readonly displayName: string;
  readonly actionId: InteractionActionId;
  readonly actionLabel: string;
  readonly durationMs: number;
  readonly interruptible: boolean;
  readonly anchorX: number;
  readonly anchorY: number;
  readonly anchorZ: number;
  readonly interactionRadius: number;
  readonly maxRange: number;
  readonly priority: number;
  readonly objectState: InteractionObjectState;
  readonly revision: number;
  readonly lineOfSight: boolean;
  readonly available: boolean;
}

export interface InteractionTarget {
  readonly candidate: InteractionCandidate;
  readonly distance: number;
  readonly facing: number;
  readonly score: number;
}

export interface InteractionTargetQuery {
  readonly playerX: number;
  readonly playerY: number;
  readonly playerZ: number;
  readonly facingYaw: number;
  readonly searchRadius?: number;
  readonly currentTargetKey?: string;
  readonly switchMargin?: number;
  /** Debug-only fallback; normal targeting always leaves this false. */
  readonly includeOccluded?: boolean;
}

/**
 * Selects from an already spatially bounded set. Distance dominates, facing
 * resolves nearby ambiguity, and a small margin keeps the previous target
 * stable without retaining an invalid target.
 */
export function selectInteractionTarget(
  candidates: readonly InteractionCandidate[],
  query: InteractionTargetQuery,
): InteractionTarget | undefined {
  const searchRadius = finitePositive(query.searchRadius, DEFAULT_INTERACTION_SEARCH_RADIUS);
  const switchMargin = Math.max(0, query.switchMargin ?? TARGET_SWITCH_MARGIN);
  const facingX = Math.sin(query.facingYaw);
  const facingZ = Math.cos(query.facingYaw);
  let best: InteractionTarget | undefined;
  let current: InteractionTarget | undefined;

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index]!;
    if (!isCandidateUsable(candidate, query.includeOccluded === true)) continue;
    const deltaX = candidate.anchorX - query.playerX;
    const deltaY = candidate.anchorY - query.playerY;
    const deltaZ = candidate.anchorZ - query.playerZ;
    const anchorDistance = Math.hypot(deltaX, deltaY, deltaZ);
    if (anchorDistance > searchRadius) continue;
    const distance = Math.max(0, anchorDistance - candidate.interactionRadius);
    if (distance > candidate.maxRange) continue;

    const horizontalDistance = Math.hypot(deltaX, deltaZ);
    const facing = horizontalDistance > 0.0001
      ? clamp((deltaX * facingX + deltaZ * facingZ) / horizontalDistance, -1, 1)
      : 1;
    const score = distance + (1 - facing) * 0.55 - candidate.priority * 0.005;
    const target = { candidate, distance, facing, score };
    if (interactionTargetKey(candidate.target) === query.currentTargetKey) current = target;
    if (!best || score < best.score - 0.000_001 || (
      Math.abs(score - best.score) <= 0.000_001
      && interactionTargetKey(candidate.target).localeCompare(interactionTargetKey(best.candidate.target)) < 0
    )) {
      best = target;
    }
  }

  if (!best || !current || interactionTargetKey(best.candidate.target) === interactionTargetKey(current.candidate.target)) return best;
  return best.score + switchMargin < current.score ? best : current;
}

function isCandidateUsable(candidate: InteractionCandidate, includeOccluded: boolean): boolean {
  return candidate.available
    && (candidate.lineOfSight || includeOccluded)
    && candidate.target.id.length > 0
    && candidate.actionId.length > 0
    && Number.isFinite(candidate.anchorX)
    && Number.isFinite(candidate.anchorY)
    && Number.isFinite(candidate.anchorZ)
    && Number.isFinite(candidate.priority)
    && candidate.interactionRadius > 0
    && candidate.maxRange > 0;
}

export function interactionTargetKey(target: InteractionTargetRef): string {
  return `${target.kind}:${target.id}`;
}

function finitePositive(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : fallback;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}
